<template>
  <div class="chometz-sales-view" :class="{ sidebarOpen: submissions.length }">
    <div class="chometz-sales-view__form">
      <ChometzCompanyForm
        v-if="loaded"
        :finalizedOrNoChometz="isFinalizedOrNoChometz"
        :plantId="selectedPlant?.Id"
        @submitted="onFormSubmitted"
        @plantChanged="onPlantChanged"
      />
    </div>
    <div
      v-if="submissions.length"
      class="chometz-sales-view__submissions right"
      :class="{ expanded: submissionsExpanded }"
    >
      <div
        class="chometz-sales-view__submissions-expand"
        @click="submissionsExpanded = !submissionsExpanded"
      >
        <font-awesome-icon
          class="expand-icon"
          :icon="submissionsExpanded ? 'chevron-right' : 'chevron-left'"
        />
      </div>
      <h2 class="chometz-sales-view__submissions-header">
        This year's Chometz Sale submissions
      </h2>
      <ChometzSubmissionsGrid
        :submissions="submissions"
        :expanded="submissionsExpanded"
      />
      <ChometzAttachmentsView
        ref="attachmentsViewRef"
        class="chometz-attachments-view"
        :companyId="selectedPlant?.Id ? 0 : Number(main.companyLogin)"
        :plantId="selectedPlant?.Id"
        :allowDelete="!isFinalizedOrNoChometz"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import ChometzCompanyForm from "./ChometzCompanyForm.vue";
import { computed, onBeforeMount, ref } from "vue";
import { api } from "@/api";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { useMainStore } from "@/stores/useMainStore";
import type { ChometzSubmission } from "@/Domain/ChometzSubmission";
import ChometzSubmissionsGrid from "./ChometzSubmissionsGrid.vue";
import type { Facility } from "@/Domain/Facility";
import { CacheService } from "@/shared/services/CacheService";
import ChometzAttachmentsView from "./ChometzAttachmentsView.vue";

const main = useMainStore();

const submissions = ref<ChometzSubmission[]>([]);
const selectedPlant = ref<Facility | null>(null);
const salesYear = ref(new Date().getFullYear());
const loaded = ref(false);
const submissionsExpanded = ref(false);
const attachmentsViewRef = ref<InstanceType<
  typeof ChometzAttachmentsView
> | null>(null);

const isFinalizedOrNoChometz = computed(() => {
  return (
    submissions.value[0]?.SubmissionFinalized || submissions.value[0]?.NoChometz
  );
});

const getSubmissionsAsync = async function () {
  handleResponsePromiseAsync(
    api.get("GetChometzSubmissionsWithFiltering", {
      userLogin: main.userLogin,
      companyLogin: !selectedPlant.value ? main.companyLogin : "",
      plantId: selectedPlant.value ? selectedPlant.value.Id : 0,
      salesYear: salesYear.value,
      includeNoChometz: true,
      searchValue: "",
      filters: "",
      skip: 0,
      take: 100,
      valuesForFieldOnly: "",
      sortField: "",
      ascending: false,
    }),
    (data) => {
      submissions.value = data.ListItems;
    },
    "ChometzSalesView.vue",
    "getSubmissionsAsync"
  );
};

const getCurrentChometzSaleSeasonAsync = async function () {
  return handleResponsePromiseAsync(
    CacheService.getResponseAsync("GetCurrentChometzSaleSeason", () =>
      api.get("GetCurrentChometzSaleSeason", {})
    ),
    (data) => {
      salesYear.value = data;
    },
    "ChometzSalesView.vue",
    "getCurrentChometzSaleSeasonAsync"
  );
};

const onFormSubmitted = () => {
  getSubmissionsAsync();
  if (attachmentsViewRef.value)
    attachmentsViewRef.value.updateAttachmentsAsync();
};

const onPlantChanged = (plant: Facility) => {
  selectedPlant.value = plant;
  if (selectedPlant.value && selectedPlant.value.Id) getSubmissionsAsync();
};

onBeforeMount(async () => {
  await getCurrentChometzSaleSeasonAsync();
  await getSubmissionsAsync();
  loaded.value = true;
});
</script>

<style scoped lang="scss">
.chometz-sales-view {
  min-height: calc(100vh - 145px);
  padding: 60px 6px;
  position: relative;

  @include media-breakpoint-up(sm) {
    padding: 60px 20px;
  }

  &.sidebarOpen {
    .chometz-sales-view__form {
      @media only screen and (min-width: 992px) {
        width: calc(100% - 400px);
      }
    }
  }

  &__submissions {
    font-size: 0.8rem;
    margin-top: 20px;
    transition: width 0.6s;

    &.right {
      @media only screen and (min-width: 992px) {
        position: absolute;
        right: 0;
        width: 400px;
        height: 100%;
        padding: 60px 8px;
        background: #efefef;
        top: 0;
        border-left: 1px solid #ddd;
        margin-top: 0;
        max-width: 900px;
      }
    }
    &-header {
      margin-bottom: 15px;
    }

    &-expand {
      position: absolute;
      top: 24px;
      left: -15px;
      background: #efefef;
      border: 1px solid #ddd;
      border-radius: 24px;
      cursor: pointer;
      z-index: 1;
      width: 30px;
      height: 30px;
      justify-content: center;
      align-items: center;
      display: none;

      @media only screen and (min-width: 992px) {
        display: flex;
      }

      &:hover {
        background: #ddd;
        border-color: #efefef;
      }
    }

    &.expanded {
      @media only screen and (min-width: 992px) {
        width: calc(100% - 100px);
        z-index: 1;
      }
    }
  }

  .chometz-attachments-view {
    margin-top: 12px;
  }
}
</style>
